/**
 * Finance (design B10): P&L matrix, cash, receivables ageing, tax base and their CSV/XLSX exports,
 * plus the invoice register CSV (decisions-log B7). Query params are snake_case (prompt §7).
 *
 * Money is integer minor units. Base-currency figures use the rate on each transaction date
 * (invoice date for billed, payment date for received, payout paid date — or the period end while
 * unpaid — for payout cost). A missing rate never becomes zero or 1:1: the native amount is
 * reported in `unconverted` and a warning `{date, currency, toCurrency}` is listed.
 */
import { z } from 'zod';
import type { FxSource } from './fx';
import type { AgeingBuckets, ReceivableGroup, ReceivablesCurrencyTotal } from './payments';
import { pluralRu } from '../ru';

// ------------------------------------------------------------------ enums and labels

export const FINANCE_GROUP_BY = ['client', 'issuer', 'person', 'business_line', 'currency'] as const;
export type FinanceGroupBy = (typeof FINANCE_GROUP_BY)[number];

export const FINANCE_CURRENCY_MODES = ['base', 'native'] as const;
export type FinanceCurrencyMode = (typeof FINANCE_CURRENCY_MODES)[number];

export const PNL_METRICS = [
  'billed',
  'received',
  'payout_cost',
  'revenue_tax',
  'margin',
  'margin_pct',
] as const;
export type PnlMetric = (typeof PNL_METRICS)[number];

/**
 * The summed metrics; margin and margin % derive from billed, revenue tax (billed × the issuer's or
 * project's rate, decisions-log M4) and payout cost.
 */
export const PNL_BASE_METRICS = ['billed', 'received', 'payout_cost', 'revenue_tax'] as const;
export type PnlBaseMetric = (typeof PNL_BASE_METRICS)[number];

export const FINANCE_EXPORT_VIEWS = ['pnl', 'cash', 'receivables', 'tax'] as const;
export type FinanceExportView = (typeof FINANCE_EXPORT_VIEWS)[number];

const PNL_METRIC_LABELS: Readonly<Record<PnlMetric, string>> = {
  billed: 'Выставлено',
  received: 'Получено',
  payout_cost: 'Затраты на выплаты',
  revenue_tax: 'Налог с выручки',
  margin: 'Маржа',
  margin_pct: 'Маржа, %',
};

const PNL_METRIC_INLINE: Readonly<Record<PnlBaseMetric, string>> = {
  billed: 'выставлено',
  received: 'получено',
  payout_cost: 'затраты на выплаты',
  revenue_tax: 'налог с выручки',
};

const FINANCE_GROUP_LABELS: Readonly<Record<FinanceGroupBy, string>> = {
  client: 'Клиент',
  issuer: 'Исполнитель',
  person: 'Сотрудник',
  business_line: 'Направление',
  currency: 'Валюта',
};

/** Segmented control "Show" (design F:157): «Выставлено», «Затраты на выплаты», «Маржа, %». */
export const pnlMetricLabel = (metric: PnlMetric): string => PNL_METRIC_LABELS[metric];

/** A base metric inside a sentence: «затраты на выплаты» (missing-rate notes and tooltips). */
export const pnlMetricInline = (metric: PnlBaseMetric): string => PNL_METRIC_INLINE[metric];

/** Segmented control "Group by" (design F:163 plus Person and Business line, decisions-log R18). */
export const financeGroupLabel = (groupBy: FinanceGroupBy): string => FINANCE_GROUP_LABELS[groupBy];

/** First header cell of the matrix (design F:166): «Клиент · проект». */
export const pnlGroupHeader = (groupBy: FinanceGroupBy): string =>
  `${FINANCE_GROUP_LABELS[groupBy]} · проект`;

/**
 * Group labels for rows without a person, client, issuer or business line (a v2 project may have no
 * client or issuer, decisions-log V9), and the row of lines without a project.
 */
export const noPersonLabel = (): string => 'Без сотрудника';
export const noClientLabel = (): string => 'Без клиента';
export const noIssuerLabel = (): string => 'Без юрлица';
export const noBusinessLineLabel = (): string => 'Без направления';
export const noProjectTitle = (): string => 'Строки без проекта';

export const MIN_FINANCE_YEAR = 2000;
export const MAX_FINANCE_YEAR = 2100;
/** Setting key of the cash opening balance in base-currency minor units (decisions-log R17). */
export const CASH_OPENING_BALANCE_SETTING = 'cash_opening_balance_minor';

// ------------------------------------------------------------------ queries

/** Query strings send blank values for empty inputs (`?issuer_id=`); treat them as omitted. */
const blankAsOmitted = (value: unknown): unknown => (value === '' ? undefined : value);

const yearSchema = z.preprocess(
  blankAsOmitted,
  z.coerce.number().int().min(MIN_FINANCE_YEAR).max(MAX_FINANCE_YEAR).optional(),
);
const idSchema = z.preprocess(blankAsOmitted, z.string().trim().min(1).max(64).optional());
const groupBySchema = z.preprocess(blankAsOmitted, z.enum(FINANCE_GROUP_BY).default('client'));
const currencyModeSchema = z.preprocess(blankAsOmitted, z.enum(FINANCE_CURRENCY_MODES).default('base'));
const metricSchema = z.preprocess(blankAsOmitted, z.enum(PNL_METRICS).default('billed'));

/** GET /finance/pnl — `year` defaults to the current year. */
export const financePnlQuerySchema = z.object({
  year: yearSchema,
  group_by: groupBySchema,
  currency: currencyModeSchema,
  issuer_id: idSchema,
});

/** GET /finance/cash */
export const financeCashQuerySchema = z.object({ year: yearSchema, issuer_id: idSchema });

/** GET /finance/receivables */
export const financeReceivablesQuerySchema = z.object({ issuer_id: idSchema, client_id: idSchema });

/** GET /finance/tax-base — without `issuer_id` every issuer's payments are listed together. */
/** `?include_payroll=1`: payments of payroll statements (salary, M5/X10) are left out otherwise. */
const includePayrollSchema = z
  .enum(['0', '1', 'true', 'false'])
  .optional()
  .transform((value) => value === '1' || value === 'true');

export const financeTaxBaseQuerySchema = z.object({
  year: yearSchema,
  issuer_id: idSchema,
  include_payroll: includePayrollSchema,
});

/** GET /finance/export.csv and /finance/export.xlsx — the table of one tab, as shown. */
export const financeExportQuerySchema = z.object({
  view: z.enum(FINANCE_EXPORT_VIEWS),
  year: yearSchema,
  group_by: groupBySchema,
  currency: currencyModeSchema,
  metric: metricSchema,
  issuer_id: idSchema,
  /** Tax tab only: like GET /finance/tax-base. */
  include_payroll: includePayrollSchema,
});
export type FinanceExportQuery = z.output<typeof financeExportQuerySchema>;

/** GET /finance/invoice-register.csv — invoices dated in `year`. */
export const invoiceRegisterQuerySchema = z.object({ year: yearSchema, issuer_id: idSchema });

/** Column order of the legacy `Report data.xlsm` sheet 1 (column O has no header). */
export const INVOICE_REGISTER_COLUMNS = [
  'date_current',
  'act_num',
  'act_num_text',
  'project',
  'rights_date',
  'start_work_date',
  'end_work_date',
  'working_days',
  'total_sum',
  'total_sum_text',
  'hour_rate',
  'hour_rate_text',
  'customer_name',
  'customer_email',
  '',
  '_current_year',
  '_last_num',
  '_date_when_report_created',
  'payer',
  'contract',
  'place',
] as const;

// ------------------------------------------------------------------ shared response parts

/** A rate that was needed and not found (strict same-date lookup). */
export interface FxWarning {
  readonly date: string;
  readonly currency: string;
  readonly toCurrency: string;
}

export interface CurrencyAmount {
  readonly currency: string;
  readonly amountMinor: number;
}

/** Native amount left out of a converted figure because its rate is missing. */
export interface PnlUnconverted extends CurrencyAmount {
  readonly metric: PnlBaseMetric;
}

// ------------------------------------------------------------------ P&L

/**
 * Figures in `currency` of the row, group or total that holds them. `marginPct` is a percentage
 * with 2 decimals (30.56 = 30.56 %), null when nothing was billed.
 */
export interface PnlFigures {
  readonly billedMinor: number;
  readonly receivedMinor: number;
  readonly payoutCostMinor: number;
  /** Tax on revenue: Σ billed line × effective rate (project override, else issuer; M4). */
  readonly revenueTaxMinor: number;
  /** billed − revenue tax − payout cost. */
  readonly marginMinor: number;
  readonly marginPct: number | null;
  readonly unconverted: readonly PnlUnconverted[];
}

export interface PnlCell extends PnlFigures {
  readonly period: string;
  /** Any invoice line, allocation or payout line lands in this cell. */
  readonly hasData: boolean;
}

export interface PnlProjectCell extends PnlCell {
  /** Live non-cancelled invoices billing this project in this period (R19 click-through). */
  readonly invoiceCount: number;
}

export interface PnlRow {
  /** Unique within the response (person grouping repeats a project under each person). */
  readonly key: string;
  readonly projectId: string | null;
  readonly title: string;
  /** null when the project has no client (decisions-log V9); `clientName` is then "No client". */
  readonly clientId: string | null;
  readonly clientName: string;
  /** null when the project has no issuer (V9) and no invoice names one; short code is then ''. */
  readonly issuerId: string | null;
  readonly issuerShortCode: string;
  readonly businessLineId: string | null;
  /** Person grouping: the person this row's share belongs to. */
  readonly personId: string | null;
  /** People with hours or payout lines on the project in the window, by name. */
  readonly people: readonly string[];
  /** The project's own currency (row meta), whatever the currency mode. */
  readonly nativeCurrency: string;
  /** Currency of every figure in the row: the base currency, or `nativeCurrency` in native mode. */
  readonly currency: string;
  /** Some amounts were converted from another currency into `currency`. */
  readonly converted: boolean;
  readonly cells: readonly PnlProjectCell[];
  readonly total: PnlFigures;
}

export interface PnlGroup {
  readonly key: string;
  readonly label: string;
  /** Native mode: the rows' common currency, else the base currency (mixed group, `converted`). */
  readonly currency: string;
  readonly converted: boolean;
  readonly cells: readonly PnlCell[];
  readonly total: PnlFigures;
  readonly rows: readonly PnlRow[];
}

export interface PnlTotals {
  readonly currency: string;
  readonly converted: boolean;
  readonly cells: readonly PnlCell[];
  readonly total: PnlFigures;
}

/** GET /finance/pnl */
export interface PnlResponse {
  readonly year: number;
  readonly groupBy: FinanceGroupBy;
  readonly currencyMode: FinanceCurrencyMode;
  readonly baseCurrency: string;
  readonly issuerId: string | null;
  /** The 12 months of the year, in order. */
  readonly periods: readonly string[];
  readonly groups: readonly PnlGroup[];
  readonly totals: PnlTotals;
  readonly warnings: readonly FxWarning[];
}

// ------------------------------------------------------------------ cash

export type CashFlowKind = 'money_in' | 'payouts_out' | 'expenses';

export interface CashUnconverted extends CurrencyAmount {
  readonly kind: CashFlowKind;
}

export interface CashMonth {
  readonly period: string;
  /** Σ payments_in.amount_minor (net of the bank fee, decisions-log R2). */
  readonly moneyInMinor: number;
  /** Σ amount_minor of payouts paid in the month. */
  readonly payoutsOutMinor: number;
  readonly expensesMinor: number;
  /** money in − payouts out − expenses. */
  readonly netMinor: number;
  /** Balance at the end of the month. */
  readonly balanceMinor: number;
  readonly unconverted: readonly CashUnconverted[];
}

/** GET /finance/cash — months from January up to the current month (all 12 for a past year). */
export interface CashResponse {
  readonly year: number;
  readonly baseCurrency: string;
  readonly issuerId: string | null;
  /** Setting cash_opening_balance_minor (decisions-log R17). */
  readonly openingSettingMinor: number;
  /** Balance on 1 January: the setting plus the net of every earlier month. */
  readonly openingBalanceMinor: number;
  readonly months: readonly CashMonth[];
  readonly totals: {
    readonly moneyInMinor: number;
    readonly payoutsOutMinor: number;
    readonly expensesMinor: number;
    readonly netMinor: number;
  };
  readonly closingBalanceMinor: number;
  readonly warnings: readonly FxWarning[];
}

// ------------------------------------------------------------------ receivables

export interface FinanceReceivableGroup extends ReceivableGroup {
  readonly invoiceCount: number;
}

/** GET /finance/receivables — ageing per client and currency (decisions-log B6). */
export interface FinanceReceivablesResponse {
  readonly asOf: string;
  readonly items: readonly FinanceReceivableGroup[];
  readonly totals: { readonly perCurrency: readonly ReceivablesCurrencyTotal[] };
}

export type { AgeingBuckets };

// ------------------------------------------------------------------ tax base

export interface TaxBaseRow {
  readonly paymentId: string;
  readonly paidOn: string;
  readonly clientId: string;
  readonly clientName: string;
  readonly issuerId: string;
  /** Amount received (net). */
  readonly amountMinor: number;
  readonly feeMinor: number;
  /** amount + fee: the taxable income (decisions-log R2). */
  readonly grossMinor: number;
  readonly currency: string;
  /** GEL (tax currency) per 1 unit of `currency` on `paidOn` (×1e6); 1e6 for GEL; null when missing. */
  readonly rateE6: number | null;
  /** Where the rate came from (`manual` rows win); null for GEL payments and missing rates. */
  readonly rateSource: FxSource | null;
  /** grossMinor in GEL; null when the rate is missing. */
  readonly gelMinor: number | null;
  readonly missing: boolean;
}

export interface TaxBaseTotals {
  /** Σ converted gross amounts; payments without a rate are listed in `unconverted`, never dropped. */
  readonly gelMinor: number;
  readonly missingCount: number;
  readonly unconverted: readonly CurrencyAmount[];
  readonly paymentCount: number;
}

export interface TaxBaseMonth extends TaxBaseTotals {
  readonly period: string;
  readonly rows: readonly TaxBaseRow[];
}

export interface TaxBaseMissing {
  readonly paymentId: string;
  readonly paidOn: string;
  readonly currency: string;
  readonly grossMinor: number;
}

/** GET /finance/tax-base */
export interface TaxBaseResponse {
  readonly year: number;
  readonly issuerId: string | null;
  /** Payments settling payroll statements (M5) are listed only when asked for (`include_payroll=1`). */
  readonly includePayroll: boolean;
  /** Payroll payments of the year left out of this report (0 when they are included). */
  readonly payrollExcluded: number;
  /** Setting tax_currency (GEL). */
  readonly taxCurrency: string;
  /** Months with payments, ascending. */
  readonly months: readonly TaxBaseMonth[];
  readonly yearToDate: TaxBaseTotals;
  readonly missing: readonly TaxBaseMissing[];
  /** Distinct dates without a rate, ascending. */
  readonly missingDates: readonly string[];
}

/** «1 платёж без официального курса» (design F:85). */
export function missingRatePill(count: number): string {
  return `${count} ${pluralRu(count, 'платёж', 'платежа', 'платежей')} без официального курса`;
}
